import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { unitId, actionId } from '@/annotations'
import { ModalHeader, ModalBody, ModalFooter } from '@/components/Modal'
import { Modal } from '@/screens/shared/ModalScrim'
import { Button } from '@/components/Button'
import { Chip } from '@/components/Chip'
import { Note } from '@/components/Note'
import { Strip } from '@/components/Strip'
import { GridTable } from '@/components/GridTable'
import { getApiClient, ApiProblemError } from '@/data/apiClient'
import { UnexpectedRefusal, isUnexpectedRefusal } from '@/screens/auth-shared/Refusals'
import { M_RPOINT_COPY } from '@/copy/modals'
import { TRIGGER_LABEL, formatJobTime } from '@/screens/shared/jobFormatting'
import type { components } from '@/api/generated/schema'
import type { Problem } from '@/data/apiClient'

type RestorePoint = components['schemas']['RestorePoint']

/**
 * `m-rpoint` — process/09-SCREEN-SPECS/m-rpoint.md. Reads the same operation `v-snapshots` does
 * (`jobListRestorePoints`, `kind=DATA_BACKUP`) — "one source, two surfaces." **Selecting a row
 * writes wizard state only** (M9/rule 6): `onSelect` never calls the server, and neither does this
 * component beyond the list read.
 *
 * **A `MachineTime` value never appears on a screen** (`LLD-XCC-076`, §9.h A3 — "the screen form
 * never appears here and this form never appears on a screen"). The primary row time reads
 * `RestorePoint.started_at` (`ScreenTime`, server-formatted), never `data_snapshot.started_at`
 * (`JobRef`'s own field, machine form — for identity and ordering, not display) — falling back to
 * `formatJobTime()` only when the optional `started_at` is itself absent. The configuration-half
 * timestamp and `expires_at` have **no served `ScreenTime` twin at all** in this schema, so
 * `formatJobTime()` (a client-side format, itself the rule this file mostly avoids) is the reported
 * gap for those two columns specifically, not the general path.
 *
 * **`point.repositories` guarded, not trusted (found live, 2026-09-25 — crashed the whole modal,
 * "Cannot read properties of undefined (reading 'numerator')").** `RestorePoint.repositories` is
 * `required` in the contract, but `services/api/handlers/job/list_restore_points.py`'s real
 * `RestorePoint(data_snapshot=..., configuration_snapshot=...)` construction never sets it (nor
 * `expired`/`started_at`/`expires_at`/`content_bytes`/`selectable`) — flagged to the backend owner,
 * not silently normalised there. This guard is the frontend's own, separate obligation: a required
 * field a real server response omits must degrade the one cell that needed it, never take down the
 * whole picker.
 */
export function MRpoint({
  orgId,
  workspaceId,
  selectedJobId,
  onDismiss,
  onSelect,
  onSeeAll,
}: {
  orgId: string
  workspaceId: string
  selectedJobId?: string
  onDismiss: () => void
  onSelect: (point: RestorePoint) => void
  onSeeAll: () => void
}) {
  const [picked, setPicked] = useState<string | undefined>(selectedJobId)

  const list = useQuery({
    queryKey: ['restore-points', orgId, workspaceId],
    queryFn: async (): Promise<readonly RestorePoint[]> => {
      const client = getApiClient()
      const { data, error } = await client.GET('/orgs/{org_id}/workspaces/{workspace_id}/snapshots', {
        params: { path: { org_id: orgId, workspace_id: workspaceId }, query: { kind: 'DATA_BACKUP', include_expired: true } },
      })
      if (error) throw new ApiProblemError(error as unknown as Problem)
      return (data?.items ?? []) as unknown as readonly RestorePoint[]
    },
  })

  const problem = list.error instanceof ApiProblemError ? list.error.problem : undefined
  const points = list.data ?? []
  const pickedPoint = points.find((p) => p.data_snapshot.job_id === picked)
  const headingId = 'm-rpoint-heading'

  return (
    <Modal dataScr={unitId('m-rpoint')} labelledBy={headingId} onDismiss={onDismiss} maxWidth={900}>
      <ModalHeader id={headingId} heading={M_RPOINT_COPY.heading}>
        {M_RPOINT_COPY.subheading}
      </ModalHeader>
      <ModalBody>
        {problem && (isUnexpectedRefusal(problem.refusal_kind) ? <UnexpectedRefusal problem={problem} /> : <p className="text-fail text-body mb-6">{problem.title}</p>)}
        <Strip>{M_RPOINT_COPY.bannerNote}</Strip>

        {list.isLoading ? (
          <p className="text-body text-muted mt-6">…</p>
        ) : problem ? null : points.length === 0 ? (
          <p className="text-body text-muted mt-6">{M_RPOINT_COPY.emptyNote}</p>
        ) : (
          <div className="mt-6">
            <GridTable
              columns={[
                {
                  key: 'pick',
                  header: '',
                  render: (point) => {
                    const jobId = point.data_snapshot.job_id
                    const disabled = point.expired || point.selectable === false
                    return <input type="radio" name="rp-pick" checked={picked === jobId} disabled={disabled} onChange={() => setPicked(jobId)} />
                  },
                },
                {
                  key: 'data',
                  header: M_RPOINT_COPY.columnData,
                  render: (point) => (
                    <>
                      <span className="font-mono">
                        {point.data_snapshot.handle} · {point.started_at ? point.started_at.display : formatJobTime(point.data_snapshot.started_at)}
                      </span>
                      {point.repositories && point.repositories.numerator < point.repositories.denominator && (
                        <Note>{M_RPOINT_COPY.partialNoteTemplate(point.repositories.denominator - point.repositories.numerator)}</Note>
                      )}
                      {point.expired && point.expires_at && <Note>{M_RPOINT_COPY.expiredNoteTemplate(formatJobTime(point.expires_at))}</Note>}
                    </>
                  ),
                },
                {
                  key: 'trigger',
                  header: M_RPOINT_COPY.columnTrigger,
                  render: (point) => <Chip>{TRIGGER_LABEL[point.data_snapshot.trigger] ?? point.data_snapshot.trigger}</Chip>,
                },
                { key: 'repositories', header: M_RPOINT_COPY.columnRepositories, render: (point) => point.repositories?.caption ?? '—' },
                {
                  key: 'configuration',
                  header: M_RPOINT_COPY.columnConfiguration,
                  render: (point) =>
                    point.configuration_snapshot ? (
                      `${point.configuration_snapshot.handle} · ${formatJobTime(point.configuration_snapshot.started_at)} · ${TRIGGER_LABEL[point.configuration_snapshot.trigger] ?? point.configuration_snapshot.trigger}`
                    ) : (
                      <span className="text-muted">{M_RPOINT_COPY.noConfigurationNote}</span>
                    ),
                },
                { key: 'expires', header: M_RPOINT_COPY.columnExpires, render: (point) => (point.expires_at ? formatJobTime(point.expires_at) : '—') },
              ]}
              rows={points}
              getRowKey={(point) => point.data_snapshot.job_id}
              rowClassName={(point) => {
                const jobId = point.data_snapshot.job_id
                const disabled = point.expired || point.selectable === false
                return picked === jobId ? 'bg-sel' : disabled ? 'bg-row' : ''
              }}
            />
          </div>
        )}
        <p className="text-body text-muted mt-6">
          <button type="button" className="underline text-link bg-transparent border-0 p-0 cursor-pointer" onClick={onSeeAll} data-act={actionId('nav.v-snapshots')}>
            See all restore points
          </button>
        </p>
      </ModalBody>
      <ModalFooter>
        <Button data-act={undefined} ghost onClick={onDismiss}>
          {M_RPOINT_COPY.cancelButton}
        </Button>
        <Button
          data-act={undefined}
          disabled={!pickedPoint}
          onClick={() => {
            if (pickedPoint) onSelect(pickedPoint)
          }}
        >
          {M_RPOINT_COPY.useButton}
        </Button>
      </ModalFooter>
    </Modal>
  )
}
